import { useState, useEffect, useRef, useCallback } from 'react';

export interface UseInactivityDetectorOptions {
  /** Only run when true (e.g. when user has active timer) */
  enabled: boolean;
  /** Minutes of no activity before considering user inactive */
  inactivityMinutes?: number;
  /** Called when user has been inactive for the threshold */
  onInactive?: () => void;
  /** Only nudge when this tab is focused. True = user may work in other tabs; we only nudge when they're on this tab and idle. Timer runs regardless. */
  requireTabVisible?: boolean;
}

const DEFAULT_INACTIVITY_MINUTES = 5;
const MS_PER_MINUTE = 60 * 1000;

const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
] as const;

/**
 * Tracks user activity (mouse, keyboard, scroll, touch) and detects when the user
 * has been inactive on this tab for a given number of minutes. Used to nudge users
 * who may have left the timer running while away. The timer is never paused by this
 * hook—it only triggers a nudge. When requireTabVisible is true (default), we only
 * nudge when this tab is focused and idle, so working in other tabs does not trigger
 * a nudge; the timer keeps running regardless of which tab the user is in.
 */
export function useInactivityDetector({
  enabled,
  inactivityMinutes = DEFAULT_INACTIVITY_MINUTES,
  onInactive,
  requireTabVisible = true,
}: UseInactivityDetectorOptions) {
  const [isInactive, setIsInactive] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(0);
  const lastActivityRef = useRef<number>(Date.now());
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onInactiveRef = useRef(onInactive);
  onInactiveRef.current = onInactive;

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setIsInactive(false);
    setResetTrigger((t) => t + 1);
  }, []);

  useEffect(() => {
    if (!enabled) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    const thresholdMs = inactivityMinutes * MS_PER_MINUTE;

    const scheduleCheck = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        if (requireTabVisible && typeof document !== 'undefined' && document.visibilityState !== 'visible') {
          scheduleCheck();
          return;
        }
        setIsInactive(true);
        onInactiveRef.current?.();
        timeoutRef.current = null;
      }, thresholdMs);
    };

    const handleActivity = () => {
      if (requireTabVisible && typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return;
      }
      lastActivityRef.current = Date.now();
      scheduleCheck();
    };

    // Initial schedule
    scheduleCheck();

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        lastActivityRef.current = Date.now();
        scheduleCheck();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [enabled, inactivityMinutes, requireTabVisible, resetTrigger]);

  return { isInactive, resetActivity };
}
